import { computed, type Ref } from 'vue';

interface CoordinateObject {
  latitude: number;
  longitude: number;
}

export function useCoordinates() {
  /**
   * Valide si une latitude est dans la plage valide
   */
  function isValidLatitude(lat: number): boolean {
    return typeof lat === 'number' && 
           !isNaN(lat) && 
           lat >= -90 && 
           lat <= 90;
  }

  /**
   * Valide si une longitude est dans la plage valide
   */
  function isValidLongitude(lng: number): boolean {
    return typeof lng === 'number' && 
           !isNaN(lng) && 
           lng >= -180 && 
           lng <= 180;
  }

  /**
   * Valide si les coordonnées sont valides et non nulles
   */
  function areValidCoordinates(latitude: number, longitude: number): boolean {
    // Vérifier que les coordonnées sont des nombres valides
    const hasValidLat = isValidLatitude(latitude);
    const hasValidLng = isValidLongitude(longitude);
    
    // Exclure les coordonnées (0, 0) qui indiquent souvent des données manquantes
    const notZeroCoordinates = !(latitude === 0 && longitude === 0);
    
    return hasValidLat && hasValidLng && notZeroCoordinates;
  }

  /**
   * Filtre un tableau d'objets ayant des propriétés latitude/longitude
   */
  function filterValidCoordinates<T extends CoordinateObject>(items: Ref<T[]>) {
    return computed(() => {
      return items.value.filter(item => 
        areValidCoordinates(item.latitude, item.longitude)
      );
    });
  }

  /**
   * Calcule le centre géographique d'un ensemble de coordonnées valides
   */
  function calculateCenter<T extends CoordinateObject>(items: T[]): { lat: number; lng: number } {
    if (!items.length) {
      return { lat: 0, lng: 0 };
    }

    const totalLat = items.reduce((sum, item) => sum + item.latitude, 0);
    const totalLng = items.reduce((sum, item) => sum + item.longitude, 0);
    
    return {
      lat: totalLat / items.length,
      lng: totalLng / items.length
    };
  }

  return {
    isValidLatitude,
    isValidLongitude,
    areValidCoordinates,
    filterValidCoordinates,
    calculateCenter
  };
}